import { useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import TabsComponent from '../../../components/stories/atoms/tabs'
import { tabsActivationModeOptions } from '../../../components/stories/atoms/tabs/constants'
import CompanyProjectsList from './project-menu-list/table/company-projects/company-project-list-table'
import {
  getIsOrgAdmin,
  getOrganizationName,
  useMeQuery
} from '../../../react-query/authentication/query/use-me-query'
import { useAuthStore } from '../../../zustand/authentication/use-auth-store'
import { viewTypeOptions, projectMenuTabOptions } from '../constants'
import ProjectMenuTileCompanyProjects from './project-menu-tile/project-menu-tile-company-projects'
import { FormattedMessage } from 'react-intl'
import MyProjectsList from './project-menu-list/table/my-projects/my-project-list-table'
import ProjectMenuTileMyProjects from './project-menu-tile/project-menu-tile-my-projects'
import colorsOptions from '../../../components/stories/fundamentals/colors-styles/constants'
import { useProjectMenuListViewControl } from '../../../hooks/use-project-menu-list-view-control'
import {
  makeSelectUploadStatusQuickView,
  makeSelectDownloadStatus
} from '../../ImportStatus/selectors'

const ProjectMenuListRoot = ({ viewType = viewTypeOptions.LIST_VIEW }) => {
  const history = useHistory()
  const organisationName = getOrganizationName()
  const { hideMyProjects } = useAuthStore()
  const { tab: activeTab } = useProjectMenuListViewControl()
  const isOrgAdmin = getIsOrgAdmin()

  const { data: user } = useMeQuery()

  const selectedTab = hideMyProjects
    ? projectMenuTabOptions.COMPANY_PROJECTS
    : activeTab

  const uploadStatus = useSelector(makeSelectUploadStatusQuickView())
  const downloadStatus = useSelector(makeSelectDownloadStatus())

  const isQuickViewVisible = uploadStatus.length || downloadStatus.length

  const isTileView = viewType === viewTypeOptions.TILE_VIEW

  const userPermissions = useMemo(
    () => user?.permissions ?? [],
    [user?.permissions]
  )

  const isAdminOrCreateor =
    userPermissions.includes('CreateProjects') || isOrgAdmin

  const borderRadius = isTileView ? '0px' : '0px 16px 16px'
  const borderTop = isTileView
    ? `1px solid ${colorsOptions.greys['grey-20']}`
    : 'none'

  const customTabStyle = {
    padding: 0,
    backgroundColor: 'transparent',
    borderRadius,
    overflow: 'visible',
    borderTop,
    paddingBottom: isQuickViewVisible ? '3rem' : 0
  }

  const handleTabChange = (tab) => {
    history.push(`/project?view=${viewType}&tab=${tab}`)
  }

  useEffect(() => {
    if (
      hideMyProjects &&
      activeTab !== projectMenuTabOptions.COMPANY_PROJECTS
    ) {
      history.replace(
        `/project?view=${viewType}&tab=${projectMenuTabOptions.COMPANY_PROJECTS}`
      )
    }
  }, [activeTab, hideMyProjects, history, viewType])

  return (
    <TabsComponent.Root
      value={selectedTab}
      onValueChange={handleTabChange}
      activationMode={tabsActivationModeOptions.AUTOMATIC}
      style={{ height: '90%' }}
    >
      <TabsComponent.List style={{ width: 'fit-content' }}>
        {!hideMyProjects && (
          <TabsComponent.Trigger
            value={projectMenuTabOptions.MY_PROJECTS}
            style={{ flex: '0 0 auto' }}
          >
            <FormattedMessage id="projects_MyProjects" />
          </TabsComponent.Trigger>
        )}
        {isAdminOrCreateor && (
          <TabsComponent.Trigger
            value={projectMenuTabOptions.COMPANY_PROJECTS}
            style={{ flex: '0 0 auto' }}
          >
            {organisationName}
          </TabsComponent.Trigger>
        )}
      </TabsComponent.List>
      <TabsComponent.Content
        value={projectMenuTabOptions.MY_PROJECTS}
        style={customTabStyle}
      >
        {isTileView ? <ProjectMenuTileMyProjects /> : <MyProjectsList />}
      </TabsComponent.Content>

      <TabsComponent.Content
        value={projectMenuTabOptions.COMPANY_PROJECTS}
        style={customTabStyle}
      >
        {isTileView ? (
          <ProjectMenuTileCompanyProjects />
        ) : (
          <CompanyProjectsList />
        )}
      </TabsComponent.Content>
    </TabsComponent.Root>
  )
}

export default ProjectMenuListRoot
